import React, { createContext, useContext, useState, useCallback } from "react";
import { fetchPortalConfig, addPortalConfig, updatePortalConfig } from "../services/portalConfigService";
import staticConfig from "../config/caseDetailsConfig.json";
import { applyTabOverrides } from "../config/clientConfig";
import { getClient } from "../config/clients";

const PortalConfigContext = createContext(null);

// Build the default portal configuration from the static JSON.
// Uses only the bajaj-specific client entry — never "Bajaj_pp".
// api_base_url is intentionally omitted so the dev proxy (/cases-api) remains
// active; production deployments should set it via a PUT after setup.
const buildDefaultConfiguration = (sourceName) => {
  const clientEntry =
    getClient(sourceName) || {};

  return {
    // `tabs` is shared by every client, so per-client tab visibility (e.g.
    // Hegic's Point of Impact / Digital Passport) is merged in from the
    // client entry's `tabOverrides` before seeding.
    tabs: applyTabOverrides(staticConfig.tabs, clientEntry.tabOverrides),
    header: staticConfig.header,
    general: staticConfig.general,
    table: staticConfig.table,
    filters: staticConfig.filters,
    branding: staticConfig.branding,
    // client includes apiSourceName (backend identifier) alongside UI sourceName
    client: clientEntry,
  };
};

export function PortalConfigProvider({ children }) {
  // Restore from localStorage so page refresh keeps config without a re-fetch
  const [portalConfig, setPortalConfig] = useState(() => {
    try {
      const stored = localStorage.getItem("portalConfig");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);

  /**
   * Load portal configuration for the given source.
   * If none exists, seeds the server with defaults from caseDetailsConfig.json
   * and immediately re-fetches so the app starts config-driven on first login.
   *
   * The `_token` parameter is accepted but ignored: portalApiClient injects
   * the token from localStorage automatically.
   */
  const loadPortalConfig = useCallback(async (sourceName, _token) => {
    if (!sourceName) {
      console.warn("[PortalConfig] Cannot load: missing sourceName");
      return;
    }
    setConfigLoading(true);
    setConfigError(null);
    try {
      console.log("[PortalConfig] Fetching config for:", sourceName);
      let data = await fetchPortalConfig(sourceName);

      if (!data) {
        // No config provisioned yet — seed with defaults from static config.
        console.log("[PortalConfig] No config found — seeding defaults for:", sourceName);
        try {
          const defaultConfiguration = buildDefaultConfiguration(sourceName);
          await addPortalConfig(sourceName, defaultConfiguration);
          console.log("[PortalConfig] Default config created — re-fetching");
          data = await fetchPortalConfig(sourceName);
        } catch (seedErr) {
          const status = seedErr.response?.status;
          if (status === 409 || status === 422) {
            // Config already exists but GET returns 500 (known server bug).
            // Overwrite via PUT and use the PUT response directly — don't retry
            // GET because the server-side 500 is persistent and would give null again.
            console.log("[PortalConfig] Config exists (conflict) — overwriting via PUT and using response directly");
            try {
              const defaultConfiguration = buildDefaultConfiguration(sourceName);
              const putResult = await updatePortalConfig(sourceName, defaultConfiguration);
              console.log("[PortalConfig] PUT succeeded — using PUT response:", putResult);
              // putResult is already normalised to { source_name, configuration } by the service
              data = putResult?.configuration ? putResult : null;
            } catch (putErr) {
              console.warn("[PortalConfig] PUT failed:", putErr.response?.status, putErr.message);
            }
          } else {
            // Network down or other error — app continues with static fallback.
            console.warn("[PortalConfig] Seed skipped:", status, seedErr.message);
          }
        }
      }

      if (data) {
        // API returns { source_name, configuration: { ... } }
        // Store the full response so apiService can access configuration.api_base_url
        const configToStore = data.configuration ? data : { configuration: data };
        setPortalConfig(configToStore);
        localStorage.setItem("portalConfig", JSON.stringify(configToStore));
        console.log("[PortalConfig] Config loaded:", configToStore);
      } else {
        setPortalConfig(null);
        localStorage.removeItem("portalConfig");
        console.info("[PortalConfig] Running without portal config — using static defaults");
      }
    } catch (err) {
      console.error("[PortalConfig] Failed to load:", err);
      setConfigError(err.message || "Failed to load portal configuration");
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const clearPortalConfig = useCallback(() => {
    setPortalConfig(null);
    setConfigError(null);
    localStorage.removeItem("portalConfig");
  }, []);

  return (
    <PortalConfigContext.Provider
      value={{
        portalConfig,
        configLoading,
        configError,
        loadPortalConfig,
        clearPortalConfig,
      }}
    >
      {children}
    </PortalConfigContext.Provider>
  );
}

export function usePortalConfig() {
  const ctx = useContext(PortalConfigContext);
  if (!ctx) {
    throw new Error("usePortalConfig must be used within PortalConfigProvider");
  }
  return ctx;
}

export default PortalConfigContext;
